import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ShieldCheck } from "lucide-react";
import { PRIVACY } from "@/constants/testIds";
import { useSheetHistory } from "@/hooks/useSheetHistory";

/**
 * What we keep and who sees it.
 *
 * Deliberately specific rather than "we collect data to improve your
 * experience" — that phrasing tells a guest nothing they can act on. Every
 * claim here is checkable against the code:
 *   - a restaurant you have ordered at can see your profile (admin/guests.py),
 *     and only that restaurant, and only after you have ordered there
 *   - the profile is sent to the AI provider, because it shapes the plate
 *   - Profile → Settings → Delete account really does remove it
 * If any of those stop being true, this text has to change with them. The AI
 * vendor is deliberately not named: which service we use is our business, that
 * something leaves the building is the guest's.
 *
 * Translations are held to the same standard as the English: this is a
 * disclosure, so a loose rendering in ru.json or id.json is a false statement
 * about what happens to a guest's data, not a stylistic slip.
 */
export const PrivacyNote = ({ className = "" }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  useSheetHistory(open, () => setOpen(false));

  return (
    <div data-testid={PRIVACY.note} className={`text-left ${className}`}>
      {/* The details open as a bottom sheet, like the language and allergy
          pickers — an inline unfold pushed the whole screen down under the
          reader's thumb. */}
      <button
        data-testid={PRIVACY.toggle}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        className="w-full flex items-start gap-2 text-left"
      >
        <ShieldCheck size={14} className="mt-0.5 shrink-0 text-neutral-400" />
        <span className="flex-1 text-[11px] leading-relaxed text-neutral-500">
          {t("privacy.summary")}
          <span className="ml-1 font-semibold text-neutral-700 underline underline-offset-2">
            {t("privacy.toggle")}
          </span>
        </span>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[90] bg-black/50 flex items-end sm:items-center justify-center"
          onClick={() => setOpen(false)}
        >
          <div
            data-testid={PRIVACY.details}
            role="dialog"
            aria-modal="true"
            aria-label={t("privacy.toggle")}
            className="w-full sm:max-w-md max-h-[80vh] overflow-y-auto bg-white rounded-t-3xl sm:rounded-3xl p-4 pb-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-xs font-bold uppercase tracking-wider text-neutral-500 px-2 py-3">
              {t("privacy.toggle")}
            </div>
            <div className="px-2 space-y-4">
              <Detail title={t("privacy.ordersTitle")}>{t("privacy.ordersBody")}</Detail>
              <Detail title={t("privacy.toldTitle")}>{t("privacy.toldBody")}</Detail>
              <Detail title={t("privacy.seesTitle")}>{t("privacy.seesBody")}</Detail>
              <Detail title={t("privacy.changeTitle")}>{t("privacy.changeBody")}</Detail>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Detail = ({ title, children }) => (
  <div>
    <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">{title}</div>
    <p className="text-[13px] leading-relaxed text-neutral-600 mt-0.5">{children}</p>
  </div>
);

/** One-line hint for a specific field, where the general note is too far away. */
export const FieldPrivacyHint = ({ children, testId }) => (
  <p data-testid={testId} className="text-[11px] leading-relaxed text-neutral-500 px-1 pt-1">
    {children}
  </p>
);

export default PrivacyNote;
